import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import {
  PAGE_KINDS,
  PREVIEW_SANDBOX,
  PREVIEW_SCHEMES,
  PageKind,
  PreviewScheme,
  isOwnFrame,
  readPreviewMessage,
} from '../components/sites/siteSpec';
import { safeReturnPath } from '../components/common/returnPath';

/**
 * The preview at full size, in its own tab, and BROWSABLE.
 *
 * WHY A ROUTE AND NOT A BLOB. "Open in a tab" used to fetch the HTML and hand
 * it to the tab as a `blob:` URL, because the preview endpoint is authenticated
 * and `window.open` cannot carry a bearer token. That worked for looking and
 * nothing else: a blob is a dead document — no session to fetch the next page
 * with, and no parent studio for its click handler to talk to — so every
 * internal link either did nothing or, once the design carried the site's
 * address, walked you off to the live site.
 *
 * A route inside this app has the session already. Clicking reads the next
 * page, exactly as it does in the studio, at the width the design is actually
 * meant to be seen at.
 */
export default function SiteScaffoldPreviewTab() {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = Number(siteIdParam);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // The way out, from the trail the opener left. `safeReturnPath` is what keeps
  // a crafted `?from=//evil.example` from turning this button into an open
  // redirect.
  const returnPath = safeReturnPath(params.get('from'));
  const backTo = returnPath || `/deliver/${siteId}/design`;
  const backLabel = returnPath
    ? params.get('from_label') || 'where you were'
    : 'the designer';

  const kind = (params.get('kind') || 'home') as PageKind;
  const scheme = (params.get('scheme') || 'auto') as PreviewScheme;
  const path = params.get('path') || '';
  // The deploy scope. Arriving with these set is what turns this tab from "the
  // site as designed" into "the site as this deploy will leave it" — the same
  // frame, told which pages count.
  const planId = Number(params.get('plan')) || null;
  const readyOnly = params.get('ready_only') === '1';

  const [html, setHtml] = useState('');
  const [title, setTitle] = useState('');
  const [counts, setCounts] = useState<{ live: number; deploy: number; notReady: number } | null>(
    null,
  );
  const [siteName, setSiteName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const set = useCallback(
    (next: Record<string, string>, push = false) => {
      const merged = new URLSearchParams(params);
      Object.entries(next).forEach(([key, value]) => {
        if (value) merged.set(key, value);
        else merged.delete(key);
      });
      // Replace rather than push for the CONTROLS, so the browser's back button
      // walks the pages you read rather than the widths you tried.
      //
      // ASKED FOR EXPLICITLY, not inferred from whether `path` is one of the
      // keys: the kind buttons clear `path` as well as setting `kind`, so
      // inferring made every flick between Home and Section index a history
      // entry the back button then had to walk back through.
      setParams(merged, { replace: !push });
    },
    [params, setParams],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getSiteScaffoldPreview(siteId, kind, scheme, path, { planId, readyOnly })
      .then((result) => {
        if (cancelled) return;
        setHtml(result.html);
        setTitle(result.title || '');
        setCounts({
          live: result.live_count || 0,
          deploy: result.deploy_count || 0,
          notReady: result.not_ready_count || 0,
        });
        setError('');
        // The server resolves a path to its own template; reflect that back
        // into the URL so a reload lands on the same page.
        if (result.kind && result.kind !== kind) set({ kind: result.kind });
      })
      .catch((e: any) => !cancelled && setError(e?.message || 'Could not render.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, kind, scheme, path, planId, readyOnly]);

  useEffect(() => {
    api
      .getSiteScaffold(siteId)
      .then((s) => setSiteName(s.site_name))
      .catch(() => setSiteName(''));
  }, [siteId]);

  // A click inside the frame. Checked against our own frames first: this
  // listener hears every message posted to the page, and without the check an
  // embedder or a window we opened could drive the URL.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isOwnFrame(event.source)) return;
      const click = readPreviewMessage(event.data);
      if (!click) return;
      // The frame has no popup grant, so off-site links are opened out here.
      if (click.kind === 'external') {
        window.open(click.url, '_blank', 'noopener,noreferrer');
      } else {
        // Reading a page IS navigation — this is the one that gets pushed.
        set({ path: click.path }, true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [set]);

  return (
    <div className="fixed inset-0 flex flex-col bg-white dark:bg-gray-900">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {siteName || 'Preview'}
        </span>
        {path && (
          <span className="text-xs text-gray-500 dark:text-gray-400 min-w-0 truncate">
            {title ? <strong>{title}</strong> : null} <span className="font-mono">{path}</span>
          </span>
        )}
        {path && (
          <button
            onClick={() => set({ path: '' })}
            className="text-xs underline text-gray-500 dark:text-gray-400"
          >
            back to {PAGE_KINDS.find((k) => k.value === kind)?.label.toLowerCase()}
          </button>
        )}

        <div className="ml-auto flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {PAGE_KINDS.map((k) => (
            <button
              key={k.value}
              onClick={() => set({ kind: k.value, path: '' })}
              className={`px-2.5 py-1 text-xs ${
                kind === k.value && !path
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {PREVIEW_SCHEMES.map((s) => (
            <button
              key={s.value}
              onClick={() => set({ scheme: s.value })}
              title={s.hint}
              className={`px-2.5 py-1 text-xs ${
                scheme === s.value
                  ? 'bg-gray-800 dark:bg-gray-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {/* WHERE BACK GOES. Four screens open this tab — the Deliver plan view,
            a /pages row, the page builder and the subject workspace's pages pane
            — and the exit used to be hardcoded to the designer, a section three
            of them had never been in. Honour the trail when the opener left one,
            and keep the designer as the fallback. */}
        <button
          onClick={() => navigate(backTo)}
          className="px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
        >
          ← Back to {backLabel}
        </button>
        {loading && <span className="text-xs text-gray-400">Rendering…</span>}
      </div>

      {/* What the frame covers, and the one thing it cannot promise.
          Naming this tab "what the deploy will look like" raises the bar on
          accuracy. Layout, type, navigation and URLs come from the same shell
          the Hugo layouts do, and the body from the same builder the deploy
          commits (`services/deployers/body.py`) — so the only gap left is that
          Goldmark, not python-markdown, renders it on the built site. Say that
          rather than leave it to be found by diffing against the live page. */}
      {planId && counts && (
        <div className="px-3 py-1 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
          This deploy publishes <strong>{counts.deploy}</strong>{' '}
          {counts.deploy === 1 ? 'page' : 'pages'}
          {counts.live > 0 && <> alongside {counts.live} already live</>}
          {counts.notReady > 0 && (
            <>
              ; {counts.notReady} not ready{readyOnly ? ' and not shown' : ''}
            </>
          )}
          . Layout, navigation and URLs are exact, and bodies are built the way
          the deploy builds them. Hugo renders that markdown itself, so heading
          anchors can differ - build it for real from the designer to be sure.
        </div>
      )}

      {error ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      ) : (
        <iframe
          title="site preview"
          sandbox={PREVIEW_SANDBOX}
          srcDoc={html}
          className="flex-1 border-0 bg-white w-full"
        />
      )}
    </div>
  );
}
